/**
 * Sidebar Component
 *
 * Container for the file tree navigation.
 * Supports resizing and collapse.
 */

import { ChevronLeft, RefreshCw, Plus, Search } from "lucide-react";
import type { ReactElement } from "react";
import { useState, useCallback, useRef, useEffect } from "react";

import { useConfigStore } from "../../store/configStore";
import { FileTree } from "../FileTree/FileTree";

interface SidebarProps {
  readonly onToggle: () => void;
}

const MIN_WIDTH = 200;
const MAX_WIDTH = 500;
const DEFAULT_WIDTH = 280;

export function Sidebar({ onToggle }: SidebarProps): ReactElement {
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const sidebarRef = useRef<HTMLDivElement>(null);

  const { refreshConfigs, isLoading } = useConfigStore();

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
  }, []);

  const resize = useCallback(
    (event: MouseEvent) => {
      if (!isResizing || !sidebarRef.current) return;

      const newWidth = event.clientX - sidebarRef.current.getBoundingClientRect().left;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        setWidth(newWidth);
      }
    },
    [isResizing]
  );

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", resize);
      document.addEventListener("mouseup", stopResizing);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", stopResizing);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <aside
      ref={sidebarRef}
      className="relative flex flex-col border-r border-border bg-background-secondary"
      style={{ width: `${width}px` }}
    >
      {/* Header */}
      <div className="flex h-10 items-center justify-between border-b border-border px-3">
        <span className="text-xs font-medium uppercase tracking-wider text-foreground-muted">
          Explorer
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => refreshConfigs()}
            disabled={isLoading}
            className="rounded p-1 text-foreground-muted hover:bg-background hover:text-foreground disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </button>
          <button
            className="rounded p-1 text-foreground-muted hover:bg-background hover:text-foreground"
            title="New Config"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={onToggle}
            className="rounded p-1 text-foreground-muted hover:bg-background hover:text-foreground"
            title="Collapse Sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="border-b border-border p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted" />
          <input
            type="text"
            placeholder="Search configs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-8 py-1.5 text-sm"
          />
        </div>
      </div>

      {/* File Tree */}
      <div className="flex-1 overflow-auto p-2">
        <FileTree searchQuery={searchQuery} />
      </div>

      {/* Resize Handle */}
      <div
        onMouseDown={startResizing}
        className={`absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-primary ${
          isResizing ? "bg-primary" : ""
        }`}
      />
    </aside>
  );
}
